import React from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { useAuth } from '../App';
import { apiRequest, downloadInvoice } from '../api';

interface Invoice {
  id_invoice: number;
  datum: string | null;
  has_pdf: boolean;
}

interface InvoicePosition {
  id_invoice: number;
  position: string;
  amount: number;
}

interface InvoiceAdjustment {
  position: string;
  amount: number;
  datum: string | null;
}

interface InvoiceRow {
  key: string;
  invoiceID: number | null;
  sortInvoiceID: number | null;
  date: string | null;
  positions: string[];
  amount: number;
  hasPDF: boolean;
}

type SortField = 'invoice' | 'date' | 'position' | 'amount';
type SortDirection = 'asc' | 'desc';

const asRecordList = (value: unknown): Record<string, unknown>[] => Array.isArray(value)
  ? value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
  : [];

const payloadList = (value: unknown, key: string): unknown => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    const nested = (value as Record<string, unknown>)[key];
    if (Array.isArray(nested)) return nested;
  }
  return [];
};

const normalizeInvoices = (value: unknown): Invoice[] => asRecordList(payloadList(value, 'invoices')).map((item) => ({
  id_invoice: Number(item.id_invoice) || 0,
  datum: typeof item.datum === 'string' ? item.datum : null,
  has_pdf: Boolean(item.has_pdf),
}));

const normalizePositions = (value: unknown): InvoicePosition[] => asRecordList(value).map((item) => ({
  id_invoice: Number(item.id_invoice) || 0,
  position: String(item.position ?? ''),
  amount: Number(item.amount) || 0,
}));

const normalizeAdjustments = (value: unknown): InvoiceAdjustment[] => asRecordList(payloadList(value, 'adjustments')).map((item) => ({
  position: String(item.position ?? ''),
  amount: Number(item.amount) || 0,
  datum: typeof item.datum === 'string' ? item.datum : null,
}));

const formatDate = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
};

const formatCurrency = (value: number) => value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const RechnungenPage: React.FC = () => {
  const { token } = useAuth();
  const [invoices, setInvoices] = React.useState<Invoice[]>([]);
  const [positions, setPositions] = React.useState<InvoicePosition[]>([]);
  const [adjustments, setAdjustments] = React.useState<InvoiceAdjustment[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [downloadingID, setDownloadingID] = React.useState<number | null>(null);
  const [sortField, setSortField] = React.useState<SortField>('date');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');

  React.useEffect(() => {
    if (!token) return;
    Promise.all([
      apiRequest<unknown>('/account/invoices', token),
      apiRequest<unknown>('/account/invoices/positions', token),
      apiRequest<unknown>('/account/invoices/adjustments', token),
    ]).then(([invoicePayload, positionPayload, adjustmentPayload]) => {
      setInvoices(normalizeInvoices(invoicePayload));
      setPositions(normalizePositions(positionPayload));
      setAdjustments(normalizeAdjustments(adjustmentPayload));
    }).catch((requestError: Error) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [token]);

  const rows = React.useMemo(() => {
    const positionsByInvoice = new Map<number, InvoicePosition[]>();
    positions.forEach((position) => {
      const existing = positionsByInvoice.get(position.id_invoice) ?? [];
      existing.push(position);
      positionsByInvoice.set(position.id_invoice, existing);
    });

    const invoiceRows: InvoiceRow[] = invoices.map((invoice) => {
      const invoicePositions = (positionsByInvoice.get(invoice.id_invoice) ?? [])
        .filter((position) => position.amount >= 0);
      return {
        key: `invoice-${invoice.id_invoice}`,
        invoiceID: invoice.id_invoice,
        sortInvoiceID: invoice.id_invoice,
        date: invoice.datum,
        positions: invoicePositions.map((position) => position.position).filter(Boolean),
        amount: invoicePositions.reduce((sum, position) => sum + position.amount, 0),
        hasPDF: invoice.has_pdf,
      };
    });

    const adjustmentRows: InvoiceRow[] = adjustments.map((adjustment, index) => ({
      key: `adjustment-${index}`,
      invoiceID: null,
      sortInvoiceID: null,
      date: adjustment.datum,
      positions: adjustment.position.trim() ? [adjustment.position.trim()] : [],
      amount: adjustment.amount,
      hasPDF: false,
    }));

    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...invoiceRows, ...adjustmentRows].sort((first, second) => {
      let comparison = 0;
      switch (sortField) {
        case 'invoice':
          if (first.sortInvoiceID === null) return second.sortInvoiceID === null ? 0 : 1;
          if (second.sortInvoiceID === null) return -1;
          comparison = first.sortInvoiceID - second.sortInvoiceID;
          break;
        case 'date':
          comparison = new Date(first.date ?? 0).getTime() - new Date(second.date ?? 0).getTime();
          break;
        case 'position':
          comparison = first.positions.join(' ').localeCompare(second.positions.join(' '), 'de');
          break;
        case 'amount':
          comparison = first.amount - second.amount;
          break;
        default:
          break;
      }
      return comparison * direction;
    });
  }, [adjustments, invoices, positions, sortDirection, sortField]);

  const balance = React.useMemo(() => rows.reduce((total, row) => total + row.amount, 0), [rows]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((direction) => direction === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortableHeader = (field: SortField, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell align={align} sortDirection={sortField === field ? sortDirection : false}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDirection : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const handleDownload = async (invoiceID: number) => {
    if (!token) return;
    setDownloadingID(invoiceID);
    setError(null);
    try {
      await downloadInvoice(invoiceID, token);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Download fehlgeschlagen.');
    } finally {
      setDownloadingID(null);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>Rechnungen</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Übersicht Ihrer Rechnungen und Buchungen.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && (
        <Box sx={{ mb: 2, px: 3, py: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" color="text.secondary">{balance >= 0 ? 'Gesamt offen' : 'Guthaben'}</Typography>
          <Typography variant="h5" fontWeight={700} color={balance > 0 ? 'error.main' : balance < 0 ? 'success.main' : 'text.primary'}>
            {formatCurrency(Math.abs(balance))}
          </Typography>
        </Box>
      )}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              {sortableHeader('invoice', 'Rechnungsnummer')}
              {sortableHeader('date', 'Datum')}
              {sortableHeader('position', 'Positionen')}
              {sortableHeader('amount', 'Betrag', 'right')}
              <TableCell align="right">PDF</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={28} sx={{ my: 2 }} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">Es liegen noch keine Rechnungen vor.</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.key} hover>
                <TableCell>{row.invoiceID ?? ''}</TableCell>
                <TableCell>{formatDate(row.date)}</TableCell>
                <TableCell sx={{ maxWidth: 440 }}>
                  {row.positions.length > 0 ? row.positions.map((position, index) => (
                    <Typography key={`${row.key}-position-${index}`} variant="body2">{position}</Typography>
                  )) : '—'}
                </TableCell>
                <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                <TableCell align="right">
                  {row.hasPDF && row.invoiceID !== null ? (
                    <Button size="small" startIcon={<DownloadOutlinedIcon />} onClick={() => void handleDownload(row.invoiceID!)} disabled={downloadingID === row.invoiceID}>
                      {downloadingID === row.invoiceID ? 'Lädt …' : 'PDF laden'}
                    </Button>
                  ) : row.invoiceID !== null ? <Typography variant="body2" color="text.secondary">Nicht verfügbar</Typography> : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RechnungenPage;
